import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth, user } from '@angular/fire/auth';
import { toSignal } from '@angular/core/rxjs-interop';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { UserService } from '../../services/user.service';
import {
  BILLING_ENTITY_TYPE_LABELS,
  BillingEntityType,
  DEFAULT_BILLING_COUNTRY,
} from '../../models/billing.model';
import { DEFAULT_PAYOUT_MODE, PayoutMode, UserRole } from '../../models/user.model';
import { emptyBillingProfile, parseBillingProfile } from '../../shared/billing/billing-profile.utils';
import { spanishTaxIdForEntityValidator } from '../../shared/validation/spanish-tax-id';

/**
 * User-supplied slug pattern (must match the regex used by the
 * `updateOwnerSlug` Cloud Function). Lowercase alphanumeric with single
 * internal hyphens — no leading/trailing or doubled hyphens.
 */
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SLUG_MIN_LEN = 3;
const SLUG_MAX_LEN = 50;
const PROFILE_NAME_MIN_LEN = 2;
const PROFILE_NAME_MAX_LEN = 80;
const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [ReactiveFormsModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './settings.component.html',
})
export class SettingsComponent implements OnInit {
  readonly userService = inject(UserService);
  private auth         = inject(Auth);
  private route        = inject(ActivatedRoute);
  private router       = inject(Router);
  private fb           = inject(FormBuilder);
  readonly authUser    = toSignal(user(this.auth));
  readonly profile     = this.userService.currentProfile;
  readonly saving      = signal(false);
  readonly savingProfile = signal(false);
  readonly profileError = signal<string | null>(null);
  readonly profileSuccess = signal(false);
  readonly pendingPhoto = signal<File | null>(null);
  readonly photoPreviewUrl = signal<string | null>(null);
  readonly savingPayments = signal(false);
  readonly savingBilling = signal(false);
  readonly billingError = signal<string | null>(null);
  readonly billingSuccess = signal(false);
  private readonly billingSubmitAttempted = signal(false);
  readonly stripeLoading = signal(false);
  readonly stripeDisconnecting = signal(false);
  readonly stripeError = signal<string | null>(null);
  readonly payoutModeSaving = signal(false);

  /** True if the current role can view/edit payment methods (owner or admin). */
  readonly isOwner = computed(() => {
    const role = this.profile()?.role;
    return role === 'owner' || role === 'admin';
  });

  /**
   * Cobros form (owner-only). Each payment method has a toggle + its own
   * required fields. Field validators are conditional on the toggle —
   * applied dynamically in an effect below.
   */
  readonly paymentsForm = this.fb.group({
    sepaEnabled:   [false],
    accountHolder: [''],
    iban:          [''],
    bizumEnabled:  [false],
    mobilePhone:   [''],
    cashEnabled:   [false],
  });

  readonly billingForm = this.fb.group({
    entityType: ['persona_fisica' as BillingEntityType, Validators.required],
    actsAsBusiness: [false],
    legalName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(120)]],
    taxId: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(12)]],
    addressLine1: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(120)]],
    postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    city: ['Madrid', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
    province: ['Madrid', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
  });

  readonly billingEntityTypeOptions: { value: BillingEntityType; label: string }[] = [
    { value: 'persona_fisica', label: BILLING_ENTITY_TYPE_LABELS.persona_fisica },
    { value: 'persona_juridica', label: BILLING_ENTITY_TYPE_LABELS.persona_juridica },
  ];
  readonly billingProfileComplete = this.userService.billingProfileComplete;

  readonly profileForm = this.fb.group({
    displayName: ['', [
      Validators.required,
      Validators.minLength(PROFILE_NAME_MIN_LEN),
      Validators.maxLength(PROFILE_NAME_MAX_LEN),
    ]],
  });

  /** Public profile slug form (owner/admin only). */
  readonly slugForm = this.fb.group({
    slug: ['', [
      Validators.required,
      Validators.minLength(SLUG_MIN_LEN),
      Validators.maxLength(SLUG_MAX_LEN),
      Validators.pattern(SLUG_PATTERN),
    ]],
  });
  readonly savingSlug    = signal(false);
  readonly slugError     = signal<string | null>(null);
  readonly slugSuccess   = signal(false);
  readonly slugCopied    = signal(false);
  readonly SLUG_MIN_LEN  = SLUG_MIN_LEN;
  readonly SLUG_MAX_LEN  = SLUG_MAX_LEN;

  readonly displayPhotoUrl = computed(() => {
    const preview = this.photoPreviewUrl();
    if (preview) return preview;
    const url = (this.profile()?.photoURL ?? '').trim();
    return url || null;
  });

  readonly profileDirty = signal(false);

  /** Initials computed from the display name — fallback to email local-part. */
  readonly avatarInitials = computed(() => {
    const name = (this.profileForm.controls.displayName.value ?? this.profile()?.displayName ?? '').trim();
    if (name) {
      return name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
    }
    const email = this.profile()?.email ?? '';
    return (email.split('@')[0]?.[0] ?? '?').toUpperCase();
  });

  /** Human-friendly label for the role pill in the hero header. */
  readonly roleLabel = computed(() => {
    switch (this.profile()?.role) {
      case 'admin': return 'Administrador';
      case 'owner': return 'Propietario';
      case 'user':  return 'Arrendatario';
      default:      return '';
    }
  });

  /** Absolute public profile URL — used in the preview line + clipboard copy. */
  readonly publicProfileUrl = computed(() => {
    const slug = (this.profile()?.ownerSlug ?? '').trim();
    if (!slug) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/propietario/${slug}`;
  });

  /** Count of assets owned by this user; null = not yet loaded */
  readonly ownedAssetCount = signal<number | null>(null);

  /**
   * True when the user has at least one asset (active or draft).
   * Blocks switching from owner → user to protect data integrity.
   */
  readonly switchBlocked = computed(() => (this.ownedAssetCount() ?? 0) > 0);

  readonly stripeConnectStatus = computed(
    () => this.profile()?.stripeConnectStatus ?? 'none',
  );

  readonly stripeConnectLabel = computed(() => {
    switch (this.stripeConnectStatus()) {
      case 'active': return 'Cuenta Stripe lista';
      case 'restricted': return 'Verificación pendiente en Stripe';
      case 'onboarding': return 'Configuración incompleta';
      default: return 'No conectada';
    }
  });

  readonly stripeConnectDetail = computed(() => {
    const p = this.profile();
    if (!p?.stripeAccountId || this.stripeConnectStatus() === 'none') return null;
    if (this.stripeConnectStatus() === 'active') {
      return 'Cobros y pagos habilitados. Ya puedes activar el modo Stripe en Checkout.';
    }
    const charges = p.stripeChargesEnabled === true ? 'sí' : 'no';
    const payouts = p.stripePayoutsEnabled === true ? 'sí' : 'no';
    if (this.stripeConnectStatus() === 'restricted') {
      return `Stripe tiene tus datos. Cobros: ${charges} · Pagos a tu banco: ${payouts}. ` +
        'Si acabas de terminar el formulario, pulsa «Actualizar estado».';
    }
    return 'Termina el onboarding en Stripe o pulsa «Actualizar estado» si ya lo completaste.';
  });

  readonly canEnableStripePayout = computed(
    () => this.stripeConnectStatus() === 'active',
  );

  readonly canDisconnectStripe = computed(() => {
    const p = this.profile();
    if (!p) return false;
    if (typeof p.stripeAccountId === 'string' && p.stripeAccountId.startsWith('acct_')) {
      return true;
    }
    const status = p.stripeConnectStatus;
    return !!status && status !== 'none';
  });

  readonly payoutMode = computed(
    () => (this.profile()?.payout_mode ?? DEFAULT_PAYOUT_MODE) as PayoutMode,
  );

  ngOnInit(): void {
    const stripeReturn = this.route.snapshot.queryParamMap.get('stripe');
    if (stripeReturn === 'return' || stripeReturn === 'refresh') {
      void this.refreshStripeConnectStatus(true).finally(() => {
        void this.router.navigate(['/ajustes'], { replaceUrl: true });
      });
    }
  }

  async refreshStripeConnectStatus(fromStripeReturn = false): Promise<void> {
    if (this.stripeLoading()) return;
    if (!fromStripeReturn && !this.canDisconnectStripe()) return;
    this.stripeLoading.set(true);
    this.stripeError.set(null);
    try {
      await this.userService.syncStripeConnectStatus();
    } catch (err: unknown) {
      this.stripeError.set(
        this.formatCallableError(err, 'No se pudo actualizar el estado desde Stripe.'),
      );
    } finally {
      this.stripeLoading.set(false);
    }
  }

  constructor() {
    // Load the asset count once when the profile is available and the user
    // is an owner or admin. Reloads if the auth user changes.
    effect(() => {
      const uid  = this.authUser()?.uid;
      const role = this.profile()?.role;
      if (uid && (role === 'owner' || role === 'admin')) {
        this.userService.getOwnedAssetCount(uid).then(c => this.ownedAssetCount.set(c));
      } else {
        this.ownedAssetCount.set(0);
      }
    });

    // Validators on the payments form depend on which methods are toggled
    // on. Re-evaluated whenever the toggles change so disabled fields don't
    // block submission with stale errors.
    this.paymentsForm.controls.sepaEnabled.valueChanges.subscribe(() => this.applyPaymentValidators());
    this.paymentsForm.controls.bizumEnabled.valueChanges.subscribe(() => this.applyPaymentValidators());
    this.applyPaymentValidators();

    this.billingForm.controls.entityType.valueChanges.subscribe(() => this.applyBillingValidators());
    this.applyBillingValidators();

    // Hydrate from the live profile stream — only while the user has not edited
    // the form (avoids async getDoc racing with typing and wiping fields).
    effect(() => {
      const uid = this.authUser()?.uid;
      if (!uid || !this.billingForm.pristine) return;
      const base = parseBillingProfile(this.profile()?.billingProfile) ?? emptyBillingProfile();
      this.billingForm.patchValue({
        entityType: base.entityType,
        actsAsBusiness: base.actsAsBusiness,
        legalName: base.legalName,
        taxId: base.taxId,
        addressLine1: base.address.line1,
        postalCode: base.address.postalCode,
        city: base.address.city,
        province: base.address.province,
      }, { emitEvent: false });
      this.applyBillingValidators();
    });

    effect(() => {
      const uid = this.authUser()?.uid;
      if (!uid || !this.isOwner()) return;
      this.userService.getOwnerSettings(uid).then(s => {
        this.paymentsForm.patchValue({
          sepaEnabled:   s.sepaEnabled  ?? false,
          accountHolder: s.accountHolder ?? '',
          iban:          s.iban          ?? '',
          bizumEnabled:  s.bizumEnabled ?? false,
          mobilePhone:   s.mobilePhone   ?? '',
          cashEnabled:   s.cashEnabled  ?? false,
        }, { emitEvent: false });
        this.applyPaymentValidators();
        this.paymentsForm.markAsPristine();
      });
    });

    // Keep the slug input in sync with whatever the backend trigger has
    // assigned. Owners can edit it; renters will never see the form.
    effect(() => {
      const slug = this.profile()?.ownerSlug ?? '';
      if (this.slugForm.controls.slug.value !== slug) {
        this.slugForm.controls.slug.setValue(slug, { emitEvent: false });
      }
    });

    effect(() => {
      const name = (this.profile()?.displayName ?? '').trim();
      if (this.profileForm.pristine && this.profileForm.controls.displayName.value !== name) {
        this.profileForm.controls.displayName.setValue(name, { emitEvent: false });
        this.refreshProfileDirty();
      }
    });

    this.profileForm.valueChanges.subscribe(() => this.refreshProfileDirty());
    this.profileForm.statusChanges.subscribe(() => this.refreshProfileDirty());
  }

  private refreshProfileDirty(): void {
    this.profileDirty.set(this.profileForm.dirty || this.pendingPhoto() !== null);
  }

  readonly roles: { value: UserRole; label: string; description: string }[] = [
    {
      value: 'owner',
      label: 'Propietario',
      description: 'Publicas y gestionas espacios. Accede a Mis Espacios y Estadísticas.',
    },
    {
      value: 'user',
      label: 'Arrendatario',
      description: 'Reservas espacios de otros propietarios. Accede a Mis Reservas.',
    },
  ];

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.profileError.set(null);
    this.profileSuccess.set(false);

    if (!file.type.startsWith('image/')) {
      this.profileError.set('Selecciona una imagen (JPG, PNG o WebP).');
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      this.profileError.set('La imagen no puede superar 2 MB.');
      return;
    }

    const prev = this.photoPreviewUrl();
    if (prev) URL.revokeObjectURL(prev);

    this.pendingPhoto.set(file);
    this.photoPreviewUrl.set(URL.createObjectURL(file));
    this.refreshProfileDirty();
  }

  async saveProfile(): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.savingProfile()) return;

    this.profileError.set(null);
    this.profileSuccess.set(false);

    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }
    if (!this.profileDirty()) return;

    this.savingProfile.set(true);
    try {
      const patch: { displayName?: string; photoURL?: string } = {};
      const name = (this.profileForm.controls.displayName.value ?? '').trim();

      if (this.profileForm.controls.displayName.dirty) {
        patch.displayName = name;
      }

      const photo = this.pendingPhoto();
      if (photo) {
        patch.photoURL = await this.userService.uploadProfilePhoto(uid, photo);
      }

      if (Object.keys(patch).length > 0) {
        await this.userService.updateUserProfile(uid, patch);
      }

      const preview = this.photoPreviewUrl();
      if (preview) URL.revokeObjectURL(preview);
      this.pendingPhoto.set(null);
      this.photoPreviewUrl.set(null);
      this.profileForm.markAsPristine();
      this.refreshProfileDirty();
      this.profileSuccess.set(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      this.profileError.set(msg || 'No se pudo guardar el perfil. Inténtalo de nuevo.');
    } finally {
      this.savingProfile.set(false);
    }
  }

  async changeRole(role: UserRole): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.saving() || this.profile()?.role === role) return;
    // Integrity guard: prevent downgrade while assets exist
    if (role === 'user' && this.switchBlocked()) return;
    this.saving.set(true);
    try {
      await this.userService.updateRole(uid, role);
    } finally {
      this.saving.set(false);
    }
  }

  async startStripeOnboarding(linkType: 'account_onboarding' | 'account_update' = 'account_onboarding'): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.stripeLoading()) return;
    this.stripeLoading.set(true);
    this.stripeError.set(null);
    try {
      const url = await this.userService.createConnectOnboardingLink(
        window.location.origin,
        linkType,
      );
      window.location.assign(url);
    } catch (err: unknown) {
      this.stripeError.set(this.formatCallableError(err, 'No se pudo abrir Stripe. Inténtalo de nuevo.'));
    } finally {
      this.stripeLoading.set(false);
    }
  }

  async disconnectStripe(): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.stripeDisconnecting() || !this.canDisconnectStripe()) return;

    const ok = window.confirm(
      '¿Desvincular Stripe?\n\n' +
      'Las nuevas reservas volverán al cobro manual (comisión online + saldo al propietario). ' +
      'Las reservas ya creadas conservan el modo con el que se pagaron.\n\n' +
      'La cuenta Express en Stripe no se borra; solo dejamos de usarla en TimeFlexRent.',
    );
    if (!ok) return;

    this.stripeDisconnecting.set(true);
    this.stripeError.set(null);
    try {
      await this.userService.disconnectStripeConnect();
    } catch (err: unknown) {
      this.stripeError.set(
        this.formatCallableError(err, 'No se pudo desvincular Stripe. Inténtalo de nuevo.'),
      );
    } finally {
      this.stripeDisconnecting.set(false);
    }
  }

  async setPayoutMode(mode: PayoutMode): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.payoutModeSaving() || (mode === 'STRIPE' && !this.canEnableStripePayout())) return;
    this.payoutModeSaving.set(true);
    this.stripeError.set(null);
    try {
      await this.userService.updatePayoutMode(uid, mode);
    } catch {
      this.stripeError.set('No se pudo guardar el modo de cobro.');
    } finally {
      this.payoutModeSaving.set(false);
    }
  }

  useBankAccountHolderForBilling(): void {
    const holder = (this.paymentsForm.controls.accountHolder.value ?? '').trim();
    if (!holder) return;
    this.billingForm.controls.legalName.setValue(holder);
    this.billingForm.controls.legalName.markAsDirty();
  }

  async saveBilling(): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.savingBilling()) return;
    this.billingSubmitAttempted.set(true);
    this.applyBillingValidators();
    if (this.billingForm.invalid) {
      this.billingForm.markAllAsTouched();
      this.billingError.set(this.billingFormErrorSummary());
      return;
    }
    this.savingBilling.set(true);
    this.billingError.set(null);
    this.billingSuccess.set(false);
    try {
      const v = this.billingForm.getRawValue();
      const entityType = v.entityType ?? 'persona_fisica';
      const actsAsBusiness = entityType === 'persona_juridica' || v.actsAsBusiness === true;
      await this.userService.updateBillingProfile(uid, {
        legalName: (v.legalName ?? '').trim(),
        taxId: (v.taxId ?? '').trim(),
        entityType,
        actsAsBusiness,
        fiscalRegime: entityType === 'persona_juridica' || actsAsBusiness
          ? 'empresa_autonomo'
          : 'particular',
        address: {
          line1: (v.addressLine1 ?? '').trim(),
          postalCode: (v.postalCode ?? '').trim(),
          city: (v.city ?? '').trim(),
          province: (v.province ?? '').trim(),
          country: DEFAULT_BILLING_COUNTRY,
        },
      });
      this.billingForm.markAsPristine();
      this.billingSubmitAttempted.set(false);
      this.billingSuccess.set(true);
    } catch (err: unknown) {
      this.billingError.set(this.formatFirestoreError(err, 'No se pudieron guardar los datos de facturación.'));
    } finally {
      this.savingBilling.set(false);
    }
  }

  showBillingError(
    controlName: 'legalName' | 'taxId' | 'addressLine1' | 'postalCode' | 'city' | 'province',
  ): boolean {
    const c = this.billingForm.controls[controlName];
    return c.invalid && (c.touched || c.dirty || this.billingSubmitAttempted());
  }

  billingFieldError(
    controlName: 'legalName' | 'taxId' | 'addressLine1' | 'postalCode' | 'city' | 'province',
  ): string {
    const c = this.billingForm.controls[controlName];
    if (c.hasError('required')) return 'Campo obligatorio.';
    if (c.hasError('minlength')) return 'Demasiado corto.';
    if (c.hasError('maxlength')) return 'Demasiado largo.';
    if (c.hasError('pattern')) {
      return controlName === 'postalCode' ? 'Usa 5 dígitos (ej. 28001).' : 'Formato no válido.';
    }
    if (c.hasError('spanishTaxId')) {
      return this.billingForm.controls.entityType.value === 'persona_fisica'
        ? 'DNI o NIE no válido.'
        : 'CIF no válido.';
    }
    return 'Revisa este campo.';
  }

  private billingFormErrorSummary(): string {
    const fields: Array<'legalName' | 'taxId' | 'addressLine1' | 'postalCode' | 'city' | 'province'> =
      ['legalName', 'taxId', 'addressLine1', 'postalCode', 'city', 'province'];
    const first = fields.find(f => this.billingForm.controls[f].invalid);
    return first ? this.billingFieldError(first) : 'Revisa los datos de facturación.';
  }

  private formatFirestoreError(err: unknown, fallback: string): string {
    if (err && typeof err === 'object' && 'code' in err) {
      const code = String((err as { code: string }).code);
      if (code === 'permission-denied') {
        return 'No tienes permiso para guardar (revisa la sesión o contacta con soporte).';
      }
    }
    return this.formatCallableError(err, fallback);
  }

  private applyBillingValidators(): void {
    const entityType = () =>
      (this.billingForm.controls.entityType.value ?? 'persona_fisica') as BillingEntityType;
    this.billingForm.controls.taxId.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(12),
      spanishTaxIdForEntityValidator(entityType),
    ]);
    this.billingForm.controls.taxId.updateValueAndValidity({ emitEvent: false });
  }

  async savePayments(): Promise<void> {
    const uid = this.authUser()?.uid;
    if (!uid || this.savingPayments() || !this.isOwner()) return;
    if (this.paymentsForm.invalid) { this.paymentsForm.markAllAsTouched(); return; }
    this.savingPayments.set(true);
    try {
      const v = this.paymentsForm.value;
      const sepaOn  = !!v.sepaEnabled;
      const bizumOn = !!v.bizumEnabled;
      const cashOn  = !!v.cashEnabled;
      await this.userService.updateOwnerSettings(uid, {
        sepaEnabled:   sepaOn,
        accountHolder: sepaOn ? (v.accountHolder ?? '').trim() : '',
        iban:          sepaOn ? (v.iban ?? '').replace(/[\s-]/g, '').toUpperCase() : '',
        bizumEnabled:  bizumOn,
        mobilePhone:   bizumOn ? (v.mobilePhone ?? '').trim() : '',
        cashEnabled:   cashOn,
      });
      this.paymentsForm.markAsPristine();
    } finally {
      this.savingPayments.set(false);
    }
  }

  /**
   * Applies required-when-enabled validators on the payments form. Called
   * on init and whenever a method toggle flips so disabled fields don't
   * carry stale errors blocking submission.
   */
  private applyPaymentValidators(): void {
    const sepaOn  = !!this.paymentsForm.controls.sepaEnabled.value;
    const bizumOn = !!this.paymentsForm.controls.bizumEnabled.value;

    const accountHolder = this.paymentsForm.controls.accountHolder;
    const iban          = this.paymentsForm.controls.iban;
    const mobilePhone   = this.paymentsForm.controls.mobilePhone;

    accountHolder.setValidators(
      sepaOn ? [Validators.required, Validators.minLength(3)] : [],
    );
    iban.setValidators(
      sepaOn
        ? [Validators.required, Validators.pattern(/^ES\d{2}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}$/i)]
        : [],
    );
    mobilePhone.setValidators(
      bizumOn
        ? [Validators.required, Validators.pattern(/^[67]\d{8}$/)]
        : [],
    );

    accountHolder.updateValueAndValidity({ emitEvent: false });
    iban.updateValueAndValidity({ emitEvent: false });
    mobilePhone.updateValueAndValidity({ emitEvent: false });
  }

  async saveSlug(): Promise<void> {
    if (this.savingSlug() || !this.isOwner()) return;
    this.slugError.set(null);
    this.slugSuccess.set(false);
    if (this.slugForm.invalid) {
      this.slugForm.controls.slug.markAsTouched();
      this.slugError.set(this.localSlugError());
      return;
    }
    const slug = (this.slugForm.controls.slug.value ?? '').trim().toLowerCase();
    if (slug === (this.profile()?.ownerSlug ?? '')) {
      // Nothing to do — silently ignore.
      return;
    }
    this.savingSlug.set(true);
    try {
      await this.userService.updateOwnerSlug(slug);
      this.slugSuccess.set(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      // Firebase Functions wraps the HttpsError message verbatim — show it.
      this.slugError.set(message || 'No se pudo guardar el slug.');
    } finally {
      this.savingSlug.set(false);
    }
  }

  async copyPublicUrl(): Promise<void> {
    const url = this.publicProfileUrl();
    if (!url || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(url);
      this.slugCopied.set(true);
      setTimeout(() => this.slugCopied.set(false), 1800);
    } catch {
      /* clipboard may be blocked — silently no-op */
    }
  }

  /** Surfaces Firebase callable `HttpsError.message` when present. */
  private formatCallableError(err: unknown, fallback: string): string {
    if (err && typeof err === 'object' && 'message' in err) {
      const msg = String((err as { message: string }).message).trim();
      if (msg && msg !== 'internal' && !msg.startsWith('INTERNAL')) {
        return msg;
      }
    }
    return fallback;
  }

  /** Maps the most relevant active form error to a user-facing message. */
  private localSlugError(): string {
    const c = this.slugForm.controls.slug;
    if (c.hasError('required'))  return 'Escribe un slug.';
    if (c.hasError('minlength')) return `Mínimo ${SLUG_MIN_LEN} caracteres.`;
    if (c.hasError('maxlength')) return `Máximo ${SLUG_MAX_LEN} caracteres.`;
    if (c.hasError('pattern')) {
      return 'Solo minúsculas, números y guiones (sin guiones al principio, al final o dobles).';
    }
    return 'Slug inválido.';
  }
}
